import type {
  DeployBackup,
  DeployCostRates,
  DeployDisk,
  DeployService,
  InstanceClass,
} from "@app/types";

export const hoursPerMonth = 730;

export const profileCostPerGBHour = (
  rates: DeployCostRates,
  instanceClass: InstanceClass,
) => {
  let profileRate = rates.m_class_gb_per_hour;
  if (instanceClass.startsWith("r")) {
    profileRate = rates.r_class_gb_per_hour;
  } else if (instanceClass.startsWith("c")) {
    profileRate = rates.c_class_gb_per_hour;
  }
  return profileRate;
};

export type ServiceCostProps = Pick<
  DeployService,
  "containerCount" | "containerMemoryLimitMb" | "instanceClass"
>;
export type DiskCostProps = Pick<DeployDisk, "size" | "provisionedIops">;
export type EndpointCostProps = { shared: boolean | null };
export type BackupCostProps = Pick<DeployBackup, "size">;

export type EstimateMonthlyCostProps = {
  rates: DeployCostRates;
  services?: ServiceCostProps[];
  // Database disks — priced with disk_cost_gb_per_month.
  disks?: DiskCostProps[];
  // Persistent disks — priced with the dedicated
  // persistent_disk_cost_gb_per_month rate.
  persistentDisks?: DiskCostProps[];
  endpoints?: EndpointCostProps[];
  backups?: BackupCostProps[];
};

export const estimateMonthlyCost = ({
  rates,
  services = [],
  disks = [],
  persistentDisks = [],
  endpoints = [],
  backups = [],
}: EstimateMonthlyCostProps) => {
  let hourlyCost = 0.0;

  for (const service of services) {
    const profileRate = profileCostPerGBHour(rates, service.instanceClass);

    hourlyCost +=
      ((service.containerCount * service.containerMemoryLimitMb) / 1024) *
      profileRate;
  }

  for (const endpoint of endpoints) {
    hourlyCost += endpoint.shared
      ? rates.shared_vhost_cost_per_hour
      : rates.vhost_cost_per_hour;
  }

  let monthlyCost = hourlyCost * hoursPerMonth;

  for (const disk of disks) {
    monthlyCost += disk.size * rates.disk_cost_gb_per_month;
    monthlyCost +=
      Math.max(disk.provisionedIops - 3000, 0) * rates.disk_iops_cost_per_month;
  }

  for (const disk of persistentDisks) {
    monthlyCost += disk.size * rates.persistent_disk_cost_gb_per_month;
    monthlyCost +=
      Math.max(disk.provisionedIops - 3000, 0) * rates.disk_iops_cost_per_month;
  }

  for (const backup of backups) {
    monthlyCost += backup.size * rates.backup_cost_gb_per_month;
  }

  return monthlyCost;
};
